import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, Modal } from '@payaca/ambrosia';
import { isQuoteOrEstimate } from '@payaca/helpers/jobStatusHelper';
import * as jobsStoreActions from '@payaca/store/jobs/jobsActions';

import { useSelector } from '@/api/state';
import { getJob } from '@/utils/stateAccessors';
import { getJobType } from '@payaca/helpers/jobHelperV2';
import './EditJobModal.css';
type Props = {
  isOpen: boolean;
  jobId: number;
  dealId?: number;
  /**
   * Public ID of the Project the job belongs to. When provided and the user has
   * opted in to the v2 builder, editing a sent Proposal opens the v2 builder
   * rather than the legacy editor.
   */
  projectPublicId?: string;
  onClose: () => void;
};
const EditJobModal: FC<Props> = ({
  isOpen,
  jobId,
  dealId,
  projectPublicId,
  onClose,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();

  const job = useSelector((state) => {
    return getJob(state, jobId);
  });

  const jobType = useMemo(() => {
    if (!job) return 'job';
    return getJobType(job).toLowerCase();
  }, [job]);
  const [isEditingJob, setIsEditingJob] = useState(false);
  const editJobSuccess = useSelector((state) => state.jobsStore.editJobSuccess);
  const editJobErrorMessage = useSelector(
    (state) => state.jobsStore.editJobErrorMessage
  );

  const editJob = useCallback(() => {
    if (isEditingJob) return;
    setIsEditingJob(true);
    dispatch(jobsStoreActions.requestEditJob(jobId));
  }, [dispatch, jobId, isEditingJob]);

  useEffect(() => {
    setIsEditingJob(false);
    // new job id returned from request - navigate to new job page
    if (editJobSuccess) {
      onClose();
      const isProposal = isQuoteOrEstimate(editJobSuccess.status);
      if (isProposal && projectPublicId) {
        history.replace(
          `/projects/${projectPublicId}/proposals/${editJobSuccess.publicId}`
        );
        return;
      }
      const jobTypePath = isProposal ? 'quotes' : 'invoices';
      history.replace({
        pathname: `/${jobTypePath}/${editJobSuccess.id}`,
        search: dealId ? `?dealId=${dealId}` : '',
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dealId', and 'onClose'
  }, [history, editJobSuccess]);

  useEffect(() => {
    return () => {
      dispatch(jobsStoreActions.clearEditJob());
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      title={`Edit ${jobType}`}
      size="sm"
      onClose={onClose}
      className="edit-job-modal"
    >
      <Modal.Body>
        <p>
          If you edit this {jobType}, your customer will no longer be able to
          access it using the link that has been sent to them. When you send the
          job again, the customer will receive a new access link. Are you sure
          you want to edit this {jobType}?
        </p>
        {editJobErrorMessage && (
          <div className="error">
            Sorry, there was an error editing your {jobType}. Please try again.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            onClick={editJob}
            isProcessing={isEditingJob}
            replaceContentWhenProcessing
          >
            Yes, edit {jobType}
          </Button>
          <Button variant="link" onClick={onClose}>
            No, cancel
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default EditJobModal;
